import { styled } from '@mui/material/styles';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from '@mui/material';
import { useField } from 'formik';

export const BootstrapSelect = styled(Select)(({ theme }) => ({
  '&.MuiInputBase-root': {
    width: '100%',
    '& .MuiSelect-select': {
      border: '1px solid #5E9BC6',
      fontSize: 16,
      padding: '10px 32px 10px 12px',
      borderRadius: 4,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '.MuiSelect-icon': {
      right: 4,
    },
    '@media (max-width: 768px)': {
      width: '100%',
      maxWidth: '100%',
      '& .MuiSelect-select': {
        padding: '8px 28px 8px 10px',
        fontSize: 14,
      },
      '.MuiSelect-icon': {
        right: 2,
      },
    },
    '@media (max-width: 480px)': {
      '& .MuiSelect-select': {
        padding: '8px 26px 8px 8px',
        fontSize: 13,
      },
    },
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#5E9BC6',
  },
  '&.Mui-focused,&:hover': {
    '.MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
      borderColor: theme.palette.primary.main,
    },
  },
  '&.Mui-focusVisible': {
    outline: '2px solid #153760',
    outlineOffset: '2px',
  },
  '&:has(.MuiSelect-select:focus-visible)': {
    outline: '2px solid #153760',
    outlineOffset: '2px',
  },
  svg: {
    color: '#3179C0',
  },
}));

export default function MFSelectField({
  name,
  label,
  items,
  onChange,
  disabled = false,
  indexing = false,
  applyLabelStyles = false,
  required = false,
  ...rest
}: SelectProps & {
  name: string;
  label?: string;
  items: { key: string | number; value: string | number }[];
  onChange?: (event: SelectChangeEvent<unknown>) => void;
  disabled?: boolean;
  indexing?: boolean;
  applyLabelStyles?: boolean;
  required?: boolean;
}): JSX.Element {
  const [field, meta] = useField(name);
  onChange = onChange || field.onChange;
  const errorText = meta.error && meta.touched ? meta.error : '';
  const selectId = `${name}-select`;
  const selectLabelId = `${name}-label`;
  const {
    SelectDisplayProps: selectDisplayPropsFromRest = {},
    inputProps: inputPropsFromRest,
    ...selectRest
  } = rest;
  const accessibleLabel = label?.replace(/\s*\*$/, '').trim();
  const hasCustomSelectAriaLabel = 'aria-label' in selectDisplayPropsFromRest;
  const selectDisplayProps = {
    ...selectDisplayPropsFromRest,
    ...(label && !hasCustomSelectAriaLabel
      ? { 'aria-label': undefined }
      : !label && !hasCustomSelectAriaLabel && accessibleLabel
      ? { 'aria-label': accessibleLabel }
      : {}),
  };
  return (
    <FormControl fullWidth sx={{ my: { xs: 1, sm: 1, md: 2 } }} required={required}>
      {label && (
        <InputLabel
          id={selectLabelId}
          shrink
          // htmlFor={id}
          required={required}
          sx={{
            transform: 'unset',
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
            whiteSpace: applyLabelStyles ? 'normal' : 'nowrap',
            position: applyLabelStyles ? 'relative' : 'absolute',
          }}>
          {label}
        </InputLabel>
      )}
      <BootstrapSelect
        id={selectId}
        labelId={label ? selectLabelId : undefined}
        inputProps={inputPropsFromRest}
        sx={{
          mt: applyLabelStyles ? '10px' : '32px',
          ...selectRest.sx,
          '& .Mui-disabled': {
            cursor: !disabled ? 'pointer' : 'not-allowed !important',
            pointerEvents: 'all',
          },
        }}
        MenuProps={{
          sx: {
            '.MuiPaper-root ': {
              maxWidth: { xs: '100%', sm: '31%', md: '26%' },
              maxHeight: '20%',
              overflowX: 'auto',
            },
          },
        }}
        placeholder="ssss"
        {...field}
        {...selectRest}
        onChange={onChange}
        disabled={disabled}
        SelectDisplayProps={selectDisplayProps}
        required={required}>
        {items.map((item, index: number) => (
          <MenuItem
            key={item.key}
            value={item.value}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: 'common.black',
              '&.Mui-selected,&.Mui-selected:hover': { backgroundColor: 'unset' },
            }}>
            {indexing ? (
              <>
                <Typography
                  sx={{ color: 'primary.main', fontWeight: 500, fontSize: 14 }}
                  component="span">
                  C{index < 9 ? '0' + (index + 1) : index + 1}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'common.black',
                    '&.Mui-selected,&.Mui-selected:hover': { backgroundColor: 'unset' },
                  }}
                  component="span">
                  {item.key}
                </Typography>
              </>
            ) : (
              <Typography sx={{ whiteSpace: { xs: 'pre-wrap', sm: 'nowrap' } }} component="span">
                {item.key}
              </Typography>
            )}
          </MenuItem>
        ))}
      </BootstrapSelect>
      {errorText && (
        <FormHelperText error sx={{ marginLeft: 'unset' }}>
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
